import { getMigrationsFingerprintStorageKey } from "@-/dev.improve.haniverse.lib.database.migrations";
import { createPlainConsola } from "@-/dev.improve.haniverse.lib.logger";
import onetime from "@-/com.npmjs.onetime";
import { getStorage } from "@-/dev.improve.haniverse.lib.storage";
import { wipeTursoDatabaseFiles } from "@-/dev.improve.haniverse.lib.database.engine/turso-sync";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";

const getResetAllDatabasesWebLog = onetime(() =>
  createPlainConsola({
    defaults: { tag: "reset-all-databases:web" },
  }),
);

// OPFS names the Turso engine stores the actor databases under. `content.db` and
// `static.db` are NOT here — both are served remotely by haniverse-worker, never imported
// into OPFS.
//
// BOTH files must go: `actor.db` (sync mode) and `guest.db` (signed out) are separate
// databases, and a reset that cleared only one would leave the app reopening onto
// data the user asked to delete. Each also has sidecars (`-wal`, `-changes`,
// `-wal-revert`, `-info`) that `wipeTursoDatabaseFiles` clears by prefix — a
// non-empty sidecar set is exactly what makes the next sync open skip
// `bootstrapIfEmpty`.
const OPFS_DATABASE_NAMES = ["actor.db", "guest.db"] as const;

// Both actor fingerprints: the synced database and the guest one keep independent
// migration state (see `getActorMigrationCacheKey`).
const RESET_MIGRATION_CACHE_KEY_LIST = ["actor", "actor-guest"] as const;

function removeMigrationFingerprints(): void {
  for (const cacheKey of RESET_MIGRATION_CACHE_KEY_LIST) {
    getStorage().remove(getMigrationsFingerprintStorageKey(cacheKey));
  }
}

async function removeOpfsDatabaseFiles(): Promise<string[]> {
  const removed: string[] = [];
  for (const databaseName of OPFS_DATABASE_NAMES) {
    removed.push(...(await wipeTursoDatabaseFiles(databaseName)));
  }
  return removed;
}

export type ResetAllDatabasesResult = {
  removedFiles: string[];
};

export async function resetAllDatabases(): Promise<ResetAllDatabasesResult> {
  getResetAllDatabasesWebLog().info("start");
  const removedFiles = await removeOpfsDatabaseFiles();
  removeMigrationFingerprints();
  getResetAllDatabasesWebLog().info("removed files; reloading", { removedFiles });
  globalThis.location.reload();
  return { removedFiles };
}

export function useResetAllDatabases() {
  return useMutation({ mutationFn: resetAllDatabases, mutationKey: ["resetAllDatabases"] });
}
